import { Component, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, delay, filter, distinctUntilChanged } from 'rxjs/operators';
import _ from 'lodash';
import {
  GridReadyEvent,
  IDatasource,
  ColDef,
  GridOptions
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  stringComparer,
  numberComparer,
  shallowCompare,
  ReusableStreamProperty
} from '@ipreo/muni-shared';
import { ViewOrder } from '../../../gameday/mappers/gameDay-orders.transfomations';
import { NegativeCasesService } from '../../../gameday/services/negative-cases.service';
import {
  getFormattedMaturityCoupon,
  getFormattedTotal,
  getAllOrdersOrder
} from '../../mappers/all-orders.calculations';
import { DropdownFloatingFilterComponent } from '../dropdown-floating-filter/dropdown-floating-filter.component';
import { AccountCellRendererComponent } from '../../../shared/components/renderers/account-cell.component';
import { GameDayRootReducerState } from '../../../state/root.state';
import { NegativeCasesState } from '../../../state/negative-cases/state';
import {
  getTrancheOrdersFilteredState,
  getGamedayLoadingState
} from '../../../state/data/reducer';
import { OrderNumberComponent } from '../../../shared/components/order-number/order-number.component';

function getFilterValuesExtractorFn(
  field: keyof ViewOrder,
  sortField?: keyof ViewOrder
) {
  return (orders: ViewOrder[]): string[] => {
    return _(orders)
      .sortBy(sortField || field)
      .map(field)
      .sortedUniq()
      .value() as string[];
  };
}

@Component({
  selector: 'muni-gameday-all-orders',
  templateUrl: './all-orders.template.html',
  styleUrls: ['./all-orders.styles.scss'],
  standalone: false
})
export class AllOrdersComponent implements OnDestroy {
  public totalCount: number;
  public readonly sortingOrder: string[] = ['desc', 'asc', null];

  public readonly gridOptions: GridOptions = {
    components: {
      dropdownFloatingFilter: DropdownFloatingFilterComponent
    },
    onGridReady: (params: GridReadyEvent) => {
      this.gridReadySubj.next(params);
    },
    autoSizeStrategy: { type: 'fitGridWidth' },
    onGridSizeChanged: ({ api }) => api.sizeColumnsToFit()
  };

  @ReusableStreamProperty
  private readonly allOrders: Observable<ViewOrder[]> = this.store.pipe(
    select(getTrancheOrdersFilteredState),
    filter(({ orders }) => !!orders),
    map(({ orders }) => orders.map(getAllOrdersOrder)),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly maturities: Observable<string[]> = this.allOrders.pipe(
    map(getFilterValuesExtractorFn('maturity')),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly orderTypes: Observable<string[]> = this.allOrders.pipe(
    map(getFilterValuesExtractorFn('orderType')),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly priorities: Observable<string[]> = this.allOrders.pipe(
    map(getFilterValuesExtractorFn('priority')),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly loading: Observable<boolean> = this.store.pipe(
    select(getGamedayLoadingState),
    distinctUntilChanged()
  );

  public readonly emptyMessage: Observable<string> =
    this.negativeCasesService.messages.pipe(
      map(({ orders }: NegativeCasesState) => orders)
    );

  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  private readonly gridTotalRowSubj = new Subject<any[]>();
  private readonly grid$: Observable<[GridReadyEvent, ViewOrder[]]> =
    combineLatest(this.gridReadySubj, this.allOrders).pipe(delay(0));

  private readonly total$: Observable<[GridReadyEvent, any[]]> = combineLatest(
    this.gridReadySubj,
    this.gridTotalRowSubj
  );

  private readonly gridSubscription: Subscription = this.grid$.subscribe(
    ([readyEvent, orders]: [GridReadyEvent, ViewOrder[]]) => {
      const filterMap = this.columns
        .filter((column) => column.filter)
        .map(({ field, filterParams }: any) => ({
          colId: field,
          caseSensitive: filterParams.caseSensitive
        }));

      const sortMap = this.columns.map(({ field, comparator }: any) => ({
        colId: field,
        comparator
      }));

      const dataSource: IDatasource =
        this.infiniteScrollingService.getDataSource(
          orders,
          filterMap,
          sortMap,
          (gridRows: any[]) => this.gridTotalRowSubj.next(gridRows)
        );
      readyEvent.api.setDatasource(dataSource);
      readyEvent.api.applyColumnState?.({
        state: [{ colId: 'orderNumber', sort: 'desc' }] as any,
        applyOrder: true
      });
      readyEvent.api.sizeColumnsToFit?.();
    }
  );

  private readonly totalSubscription: Subscription = this.total$
    .pipe(delay(0))
    .subscribe(([{ api }, data]) => {
      this.totalCount = data.length;
      api.setGridOption(
        'pinnedTopRowData',
        this.getPinnedTopRowData(data, this.columns)
      );
    });

  public readonly columns: ColDef[] = [
    {
      field: 'orderNumber',
      headerName: 'Ord #',
      filter: false,
      suppressHeaderMenuButton: true,
      comparator: numberComparer,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      cellRenderer: OrderNumberComponent
    },
    {
      field: 'manager',
      headerName: 'Manager',
      comparator: stringComparer,
      suppressHeaderMenuButton: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        caseSensitive: false,
        clearButton: true
      },
      floatingFilterComponentParams: {
        suppressFilterButton: true
      }
    },
    {
      field: 'series',
      headerName: 'Series',
      filter: false,
      suppressHeaderMenuButton: true,
      comparator: stringComparer,
      width: 150,
      minWidth: 150,
      maxWidth: 150
    },
    {
      field: 'maturity',
      headerName: 'Maturity',
      comparator: stringComparer,
      width: 70,
      minWidth: 70,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        caseSensitive: true
      },
      floatingFilterComponent: 'dropdownFloatingFilter' as any,
      floatingFilterComponentParams: {
        dropdownOptions: this.maturities,
        defaultOption: 'All',
        suppressFilterButton: true
      },
      suppressHeaderMenuButton: true
    },
    {
      field: 'coupon',
      headerName: 'Coupon',
      filter: false,
      suppressHeaderMenuButton: true,
      comparator: numberComparer,
      valueFormatter: getFormattedMaturityCoupon,
      width: 70,
      minWidth: 70,
      maxWidth: 70
    },
    {
      field: 'orderType',
      headerName: 'Priority',
      comparator: stringComparer,
      width: 85,
      minWidth: 85,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        caseSensitive: true
      },
      floatingFilterComponent: 'dropdownFloatingFilter' as any,
      floatingFilterComponentParams: {
        dropdownOptions: this.orderTypes,
        defaultOption: 'All',
        suppressFilterButton: true
      },
      suppressHeaderMenuButton: true
    },
    {
      field: 'priority',
      headerName: 'Type',
      comparator: stringComparer,
      width: 100,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: 'dropdownFloatingFilter' as any,
      filterParams: {
        filterOptions: ['equals'],
        caseSensitive: true
      },
      floatingFilterComponentParams: {
        dropdownOptions: this.priorities,
        defaultOption: 'All',
        suppressFilterButton: true
      },
      suppressHeaderMenuButton: true
    },
    {
      field: 'account',
      headerName: 'Account',
      comparator: stringComparer,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        caseSensitive: false,
        clearButton: true,
        suppressAndOrCondition: true
      },
      floatingFilterComponentParams: {
        suppressFilterButton: true
      },
      cellRenderer: AccountCellRendererComponent,
      suppressHeaderMenuButton: true
    },
    // {
    //   field: 'zipCode',
    //   headerName: 'Zip Code',
    //   width: 85,
    //   minWidth: 85,
    //   maxWidth: 85,
    //   comparator: stringComparer,
    //   filter: 'agTextColumnFilter',
    //   floatingFilter: true,
    //   filterParams: {
    //     filterOptions: ['startsWith'],
    //     caseSensitive: false,
    //     clearButton: true,
    //     suppressAndOrCondition: true
    //   },
    //   floatingFilterComponentParams: {
    //     suppressFilterButton: true
    //   },
    //   suppressHeaderMenuButton: true
    // },
    {
      field: 'amount',
      headerName: 'Amt ($M)',
      filter: false,
      suppressHeaderMenuButton: true,
      comparator: numberComparer,
      width: 95,
      minWidth: 95,
      maxWidth: 95,
      valueFormatter: getFormattedTotal,
      type: 'numericColumn'
    }
  ];

  constructor(
    private store: Store<GameDayRootReducerState>,
    private negativeCasesService: NegativeCasesService,
    private infiniteScrollingService: InfiniteScrollingService
  ) {}

  public ngOnDestroy(): void {
    this.gridSubscription.unsubscribe();
    this.totalSubscription.unsubscribe();
  }

  private getPinnedTopRowData(orders: ViewOrder[], colDefs: ColDef[]): any[] {
    return [
      colDefs
        .map(({ field }) => field)
        .reduce((acc, field) => {
          if (field === 'orderNumber') {
            acc[field] = `Total (${orders.length})`;
          } else if (field === 'amount') {
            acc[field] = orders
              .map(({ amount }: ViewOrder) => amount)
              .reduce((amountAcc, amount) => amountAcc + amount, 0);
          } else {
            acc[field] = undefined;
          }
          return acc;
        }, {})
    ];
  }
}
